import React, { createContext, useContext, useState, useEffect } from 'react';

const AccessibilityContext = createContext();

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

export const AccessibilityProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    fontSize: 'normal', // small, normal, large, xlarge
    highContrast: false,
    reduceMotion: false,
    screenReader: false,
    lineSpacing: 'normal', // normal, relaxed, loose
    cursorSize: 'normal', // normal, large
    linkHighlight: false
  });

  useEffect(() => {
    // Load settings from localStorage
    const saved = localStorage.getItem('accessibility_settings');
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading accessibility settings:', e);
      }
    }
    
    // Check for OS-level preferences
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setSettings(prev => ({ ...prev, reduceMotion: true }));
    }
    if (window.matchMedia('(prefers-contrast: high)').matches) {
      setSettings(prev => ({ ...prev, highContrast: true }));
    }
  }, []);

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('accessibility_settings', JSON.stringify(settings));
    
    // Apply settings to document
    const root = document.documentElement;
    
    // Font size
    const fontSizes = {
      small: '14px',
      normal: '16px',
      large: '18px',
      xlarge: '20px'
    };
    root.style.setProperty('--base-font-size', fontSizes[settings.fontSize] || '16px');
    root.style.fontSize = fontSizes[settings.fontSize] || '16px';
    
    // High contrast
    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Reduce motion
    if (settings.reduceMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }
    
    // Line spacing
    const lineHeights = {
      normal: '1.5',
      relaxed: '1.75',
      loose: '2'
    };
    root.style.setProperty('--line-height', lineHeights[settings.lineSpacing] || '1.5');
    
    // Cursor size
    if (settings.cursorSize === 'large') {
      root.classList.add('large-cursor');
    } else {
      root.classList.remove('large-cursor');
    }
    
    // Link highlight
    if (settings.linkHighlight) {
      root.classList.add('highlight-links');
    } else {
      root.classList.remove('highlight-links');
    }
  }, [settings]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const resetSettings = () => {
    setSettings({
      fontSize: 'normal',
      highContrast: false,
      reduceMotion: false,
      screenReader: false,
      lineSpacing: 'normal',
      cursorSize: 'normal',
      linkHighlight: false
    });
  };

  return (
    <AccessibilityContext.Provider value={{ settings, updateSetting, resetSettings }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export default AccessibilityContext;
